import '@material/web/icon/icon.js'

import { css, html, LitElement, nothing, PropertyValues } from 'lit'
import { customElement, property, query, state } from 'lit/decorators.js'
import { TOOL_POSITION } from '@operato/layout'
import { RouteController, ContextController } from '@operato/shell'

@customElement('ox-context-toolbar')
export class ContextToolbar extends LitElement {
  static get styles() {
    return [
      css`
        :host {
          display: flex;

          color: var(--context-toolbar-background-color, var(--md-sys-color-on-secondary-container));
          background-color: var(--context-toolbar-background-color, var(--md-sys-color-secondary-container));
          box-shadow: var(--context-toolbar-shadow-line);

          padding: 0;

          --md-icon-size: 20px;
        }

        div[container] {
          flex: 1;

          display: flex;
          position: relative;
          overflow: hidden;

          justify-content: space-between;

          gap: var(--spacing-medium);
          padding: var(--spacing-small) var(--spacing-medium);

          &:not(:has(> *)) {
            padding: 0;
          }

          div {
            display: flex;
            flex-direction: row;
            flex-wrap: nowrap;

            align-items: center;
            overflow: hidden;
            padding: 0;
          }

          #front-end md-icon,
          #front md-icon {
            border: var(--context-toolbar-function-button-border, none);
          }

          #front-end md-icon {
            color: var(--md-sys-color-on-primary-container);
            background-color: var(--md-sys-color-on-primary-container);
          }

          #front-end md-icon:hover {
            color: var(--md-sys-color-on-primary);
            background-color: var(--md-sys-color-primary);
          }

          #front md-icon {
            color: var(--md-sys-color-on-secondary-container);
            background-color: var(--md-sys-color-secondary-container);
          }

          #front md-icon:hover {
            color: var(--md-sys-on-color-secondary);
            background-color: var(--md-sys-color-secondary);
          }

          md-icon {
            color: var(--md-sys-color-on-primary-container);
            background-color: var(--md-sys-color-primary-container);
            border: 1px solid var(--md-sys-color-primary-container);
            border-radius: var(--md-sys-shape-corner-small);
            padding: var(--spacing-small);

            &:hover {
              color: var(--md-sys-color-on-primary);
              background-color: var(--md-sys-color-primary);
            }
          }

          div md-icon {
            border-radius: 0;
          }

          div md-icon:first-child {
            border-top-left-radius: var(--md-sys-shape-corner-small);
            border-bottom-left-radius: var(--md-sys-shape-corner-small);
          }

          div md-icon:last-child {
            border-top-right-radius: var(--md-sys-shape-corner-small);
            border-bottom-right-radius: var(--md-sys-shape-corner-small);
          }

          #center {
            flex: 1;
            justify-content: end;
          }
        }

        @media only screen and (max-width: 460px) {
          div[container] {
            padding: 0;
            gap: 0;
            --md-assist-chip-container-shape: 0;
          }

          div[container] div md-icon {
            border-radius: 0 !important;
          }

          div[container] div md-icon:not(:last-child) {
            border-right-width: 0;
          }
        }
      `
    ]
  }

  private _route = new RouteController(this)
  private _context = new ContextController(this)

  @query('[container]') private container!: HTMLDivElement

  render() {
    const context = this._route.context || {}

    if (context.toolbar === false) {
      return nothing
    }

    let contextKeys = Object.keys(context).filter(key => !!context[key])
    let tools = (this._context.tools || []).filter(tool => {
      if (!tool.context || contextKeys.includes(tool.context)) {
        return tool
      }
    })

    let frontEndTools = tools.filter(tool => tool.position == TOOL_POSITION.FRONT_END)
    let frontTools = tools.filter(tool => tool.position == TOOL_POSITION.FRONT)
    let centerTools = tools.filter(tool => tool.position == TOOL_POSITION.CENTER)
    let rearTools = tools.filter(tool => tool.position == TOOL_POSITION.REAR)
    let rearEndTools = tools.filter(tool => tool.position == TOOL_POSITION.REAR_END)

    return html`
      <div container>
        ${frontEndTools.length > 0
          ? html`<div id="front-end">${frontEndTools.map(tool => html` ${tool.template} `)}</div>`
          : nothing}
        ${frontTools.length > 0
          ? html`<div id="front">${frontTools.map(tool => html` ${tool.template} `)}</div>`
          : nothing}
        ${centerTools.length > 0
          ? html`<div id="center">${centerTools.map(tool => html` ${tool.template} `)}</div>`
          : nothing}
        ${rearTools.length > 0
          ? html`<div id="rear">${rearTools.map(tool => html` ${tool.template} `)}</div>`
          : nothing}
        ${rearEndTools.length > 0
          ? html`<div id="rear-end">${rearEndTools.map(tool => html` ${tool.template} `)}</div>`
          : nothing}
      </div>
    `
  }

  updated(changes: PropertyValues<this>) {
    requestAnimationFrame(() => {
      /*
        하위 엘리먼트들이 실제로 보일 필요가 없을 때는, 이 엘리먼트도 숨김 처리함.
        &:not(:has(> *)) 처리로 모두 해결되지 않으므로, 로직 처리로 보완함.
        &:not(:has(> *)) 가 필요없으나, 남겨 두었음.
      */
      if (!this.container) {
        return
      }

      const children = this.container.querySelectorAll('*')

      let zero = true

      // 모든 하위 엘리먼트의 높이를 확인
      for (const child of Array.from(children)) {
        if ((child as HTMLElement).offsetHeight !== 0) {
          zero = false
          break
        }
      }

      // 모든 하위 엘리먼트의 높이가 0이면 부모 엘리먼트의 display를 none으로 변경
      this.container.style.padding = zero ? '0' : 'revert-layer'
    })
  }

}
